"use client";

import type { DocsField, DocsObjectRef, DocsParam } from "../types";
import { RichText } from "./RichText";
import { MONO } from "./styles";

/*
 * Parameter lists and response field trees. A client module only for size:
 * the page's RSC payload then carries these as their compact data (the props)
 * instead of as fully expanded element trees, which for a large response
 * (dozens of nested fields) is several times bigger. Nothing here is
 * interactive beyond native <details>.
 */

/** How many enum values to show before folding the rest away. */
const ENUM_INLINE_MAX = 8;

function EnumValues({ values }: { values: string[] }) {
  const chips = (
    <span className={`${MONO} flex flex-wrap gap-1 text-[11.5px] text-[#d6e6f2]`}>
      {values.map((v) => (
        <code key={v} className="rounded bg-[#0e1620] px-1.5 py-px ring-1 ring-inset ring-[#2a475e]/70 [font-family:inherit]">
          {v}
        </code>
      ))}
    </span>
  );
  if (values.length <= ENUM_INLINE_MAX) {
    return (
      <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5 text-[12px] text-[#8f98a0]">
        <span>One of</span>
        {chips}
      </div>
    );
  }
  return (
    <details className="mt-1.5 text-[12px] text-[#8f98a0]">
      <summary className="cursor-pointer select-none text-[#66c0f4] hover:underline">
        One of {values.length} values
      </summary>
      <div className="mt-1.5">{chips}</div>
    </details>
  );
}

const TAG_TONE = {
  muted: "text-[#8f98a0]",
  type: "text-[#66c0f4]",
  required: "font-semibold text-[#f0a37c]",
} as const;

/** A small label in a name row (the row sets the monospace font). */
function Tag({ children, tone = "muted" }: { children: React.ReactNode; tone?: keyof typeof TAG_TONE }) {
  return <span className={`text-[11px] ${TAG_TONE[tone]}`}>{children}</span>;
}

/** The name line of a parameter or field: name, type, then flags, in one monospace row. */
const NAME_ROW = `${MONO} flex flex-wrap items-baseline gap-x-2 gap-y-0.5`;
// `<code>` gets the browser's monospace stack by default; inherit the row's font instead.
const NAME = "text-[13px] font-semibold text-[#e8eef3] [font-family:inherit]";
const PROSE = "text-[13px] leading-relaxed text-[#a9b8c4]";

export function ParamList({ params }: { params: DocsParam[] }) {
  if (params.length === 0) {
    return <p className="text-sm text-[#8f98a0]">None.</p>;
  }
  return (
    <ul className="divide-y divide-[#2a475e]/50 rounded-lg border border-[#2a475e]/70 bg-[#131c28]">
      {params.map((p) => {
        const facts = [
          p.constraints,
          p.defaultValue !== null ? `default ${p.defaultValue}` : null,
          p.example !== null ? `example ${p.example}` : null,
        ].filter((f): f is string => f !== null);
        return (
          <li key={p.name} className="px-3 py-2.5 sm:px-4">
            <div className={NAME_ROW}>
              <code className={NAME}>{p.name}</code>
              <Tag tone="type">{p.type}</Tag>
              <Tag>{p.in}</Tag>
              {p.required ? <Tag tone="required">required</Tag> : <Tag>optional</Tag>}
            </div>
            {p.description && (
              <p className={`mt-1 ${PROSE}`}>
                <RichText text={p.description} />
              </p>
            )}
            {facts.length > 0 && (
              <p className={`${MONO} mt-1 text-[11.5px] text-[#8f98a0] [overflow-wrap:anywhere]`}>{facts.join(" · ")}</p>
            )}
            {p.enumValues && p.enumValues.length > 0 && <EnumValues values={p.enumValues} />}
          </li>
        );
      })}
    </ul>
  );
}

/** A field that is a shared object links to where the page describes it. */
function ObjectLink({ object }: { object: DocsObjectRef }) {
  return (
    <a href={`#${object.anchor}`} className="text-[11px] text-[#66c0f4] underline underline-offset-2 hover:text-[#88d0ff]">
      {object.title} ›
    </a>
  );
}

function FieldHead({ field }: { field: DocsField }) {
  return (
    <div className={NAME_ROW}>
      <code className={NAME}>{field.name}</code>
      <Tag tone="type">{field.type}</Tag>
      {field.object && <ObjectLink object={field.object} />}
      {field.nullable && <Tag>nullable</Tag>}
      {field.optional && <Tag>optional</Tag>}
    </div>
  );
}

function FieldRow({ field }: { field: DocsField }) {
  const isList = field.type.startsWith("array");
  return (
    <li className="px-3 py-2 sm:px-4">
      <FieldHead field={field} />
      {field.description && (
        <p className={`mt-0.5 ${PROSE}`}>
          <RichText text={field.description} />
        </p>
      )}
      {field.enumValues && field.enumValues.length > 0 && <EnumValues values={field.enumValues} />}
      {field.children.length > 0 && (
        <details className="mt-1.5">
          <summary className="cursor-pointer select-none text-[12px] text-[#66c0f4] hover:underline">
            {isList ? "Item fields" : "Fields"} ({field.children.length})
          </summary>
          <div className="mt-2 border-l border-[#2a475e] pl-2 sm:pl-3">
            <FieldList fields={field.children} />
          </div>
        </details>
      )}
    </li>
  );
}

function FieldList({ fields }: { fields: DocsField[] }) {
  return (
    <ul className="divide-y divide-[#2a475e]/40">
      {fields.map((f) => (
        <FieldRow key={f.name} field={f} />
      ))}
    </ul>
  );
}

/** A shared object's fields (the Objects section), boxed like a response's. */
export function ObjectFields({ fields }: { fields: DocsField[] }) {
  return (
    <div className="rounded-lg border border-[#2a475e]/70 bg-[#131c28]">
      <FieldList fields={fields} />
    </div>
  );
}

/** A response's `data` or `meta`: the root line, then its fields (nested ones fold). */
export function FieldTree({ root, note }: { root: DocsField; note?: string }) {
  const isList = root.type.startsWith("array");
  return (
    <div className="rounded-lg border border-[#2a475e]/70 bg-[#131c28]">
      <div className={`px-3 py-2 sm:px-4 ${root.children.length > 0 ? "border-b border-[#2a475e]/50" : ""}`}>
        <FieldHead field={root} />
        {(root.description || note) && (
          <p className={`mt-0.5 ${PROSE}`}>
            <RichText text={[root.description, note].filter(Boolean).join(" ")} />
          </p>
        )}
        {root.enumValues && root.enumValues.length > 0 && <EnumValues values={root.enumValues} />}
        {isList && root.children.length > 0 && <p className="mt-0.5 text-[12px] text-[#8f98a0]">Each item:</p>}
      </div>
      {root.children.length > 0 && <FieldList fields={root.children} />}
    </div>
  );
}
